import React, { useState, useContext, useEffect } from 'react';
import { Form, InputNumber, Input, Button, DatePicker, Switch, Drawer, message, Modal } from 'antd';
import { LoginContext } from '../../../../../context/loginContext';
import dayjs from 'dayjs';

const CFormularioArticuloSucursal = ({ articulo, open, onClose, onSuccess }) => {
  const [form] = Form.useForm();
  const { user } = useContext(LoginContext);
  const sucursalId = user.usu_sucursal;

  useEffect(() => {
    if (articulo && open) {
      const datos = articulo.datos_sucursal || {};
      const initialValues = {
        asu_precioventa: datos.asu_precioventa,
        asu_preciocosto: datos.asu_preciocosto,
        asu_precio_siniva: datos.asu_precio_siniva,
        asu_precio_con_descuento: datos.asu_precio_con_descuento,
        asu_porc_descuento: datos.asu_porc_descuento,
        asu_stock: datos.asu_stock,
        asu_stock_minimo: datos.asu_stock_minimo,
        asu_ubicacion_interna: datos.asu_ubicacion_interna,
        asu_orden_relevancia: datos.asu_orden_relevancia,
        asu_habilitado: datos.asu_habilitado ?? true,
        asu_fecha_d_descuento: datos.asu_fecha_d_descuento ? dayjs(datos.asu_fecha_d_descuento) : null,
        asu_fecha_h_descuento: datos.asu_fecha_h_descuento ? dayjs(datos.asu_fecha_h_descuento) : null,
      };

      form.setFieldsValue(initialValues);
    } else {
      form.resetFields();
    }
  }, [articulo, open]);

  const handleFinish = async (values) => {
    // Verificar si ya existe un registro (tiene asu_id)
    const existeRegistro = articulo.datos_sucursal?.asu_id;
    
    // Preparar payload base
    const basePayload = {
      asu_fecha_d_descuento: values.asu_fecha_d_descuento ? values.asu_fecha_d_descuento.format('YYYY-MM-DD') : null,
      asu_fecha_h_descuento: values.asu_fecha_h_descuento ? values.asu_fecha_h_descuento.format('YYYY-MM-DD') : null,
    };

    let payload;
    if (existeRegistro) {
      // Para actualización, no incluir asu_sucursal ni asu_articuloem (no deben cambiar)
      payload = {
        ...values,
        ...basePayload,
      };
      // Remover campos que no deben actualizarse
      delete payload.asu_sucursal;
      delete payload.asu_articuloem;
    } else {
      // Para creación, incluir todos los campos necesarios
      payload = {
        ...values,
        asu_articuloem: articulo.articulo_empresa.aem_id,
        asu_sucursal: sucursalId,
        ...basePayload,
      };
    }

    if (payload.asu_orden_relevancia === undefined || payload.asu_orden_relevancia === null) {
      payload.asu_orden_relevancia = null;
    }

    try {
      let res;
      if (existeRegistro) {
        // Si existe, hacer PUT para actualizar
        res = await fetch(`${process.env.REACT_APP_API_URL}/articulo-sucursal/${existeRegistro}`, {
          method: 'PUT',
          headers: {
            'Authorization': `Bearer ${user.token}`,
            'Content-Type': 'application/json',
          },
          body: JSON.stringify(payload),
        });
      } else {
        // Si no existe, hacer POST para crear
        res = await fetch(`${process.env.REACT_APP_API_URL}/articulo-sucursal`, {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${user.token}`,
            'Content-Type': 'application/json',
          },
          body: JSON.stringify(payload),
        });
      }

      if (!res.ok) {
        const errorData = await res.json().catch(() => ({}));
        if (res.status === 409) {
          Modal.warning({
            title: 'Orden de relevancia duplicado',
            content: errorData.message || 'Ese número de relevancia ya existe en esta sucursal.',
          });
          return;
        }
        throw new Error(errorData.message || 'Error al guardar artículo sucursal');
      }

      message.success('Datos guardados correctamente');
      onSuccess();
      onClose();
    } catch (error) {
      console.error(error);
      message.error('Error al guardar');
    }
  };

  const handleValuesChange = (changedValues, allValues) => {
    if (!('asu_porc_descuento' in changedValues)) return;

    const precioVenta = Number(allValues.asu_precioventa);
    const porc = Number(changedValues.asu_porc_descuento);

    if (!Number.isFinite(precioVenta) || precioVenta < 0) return;
    if (!Number.isFinite(porc) || porc < 0) {
      form.setFieldsValue({ asu_precio_con_descuento: null });
      return;
    }

    const precioConDescuento = Math.round(precioVenta * (1 - porc / 100) * 100) / 100;
    form.setFieldsValue({ asu_precio_con_descuento: precioConDescuento });
  };

  return (
    <Drawer
      title="Configurar Artículo en Sucursal"
      placement="right"
      width={450}
      open={open}
      onClose={onClose}
      destroyOnClose
    >
      <Form form={form} layout="vertical" onFinish={handleFinish} onValuesChange={handleValuesChange}>
        <Form.Item
          name="asu_precioventa"
          label="Precio de Venta"
          rules={[{ required: true, message: 'Este campo es obligatorio' }]}
        >
          <InputNumber style={{ width: '100%' }} min={0} />
        </Form.Item>

        <Form.Item name="asu_preciocosto" label="Precio de Costo">
          <InputNumber style={{ width: '100%' }} min={0} />
        </Form.Item>

        <Form.Item
          name="asu_precio_siniva"
          label="Precio sin IVA"
          rules={[{ required: true, message: 'Este campo es obligatorio' }]}
        >
          <InputNumber style={{ width: '100%' }} min={0} />
        </Form.Item>

        <Form.Item name="asu_porc_descuento" label="% Descuento">
          <InputNumber style={{ width: '100%' }} min={0} max={100} />
        </Form.Item>

        <Form.Item name="asu_precio_con_descuento" label="Precio con Descuento">
          <InputNumber style={{ width: '100%' }} min={0} precision={2} />
        </Form.Item>

        <Form.Item name="asu_fecha_d_descuento" label="Desde (Descuento)">
          <DatePicker style={{ width: '100%' }} format="DD/MM/YYYY" />
        </Form.Item>

        <Form.Item name="asu_fecha_h_descuento" label="Hasta (Descuento)">
          <DatePicker style={{ width: '100%' }} format="DD/MM/YYYY" />
        </Form.Item>

        <Form.Item name="asu_stock" label="Stock">
          <InputNumber style={{ width: '100%' }} min={0} />
        </Form.Item>

        <Form.Item name="asu_stock_minimo" label="Stock Mínimo">
          <InputNumber style={{ width: '100%' }} />
        </Form.Item>

        <Form.Item name="asu_ubicacion_interna" label="Ubicación Interna">
          <Input />
        </Form.Item>

        <Form.Item
          name="asu_orden_relevancia"
          label="Orden de relevancia"
          tooltip="En el inicio público, menor número = más arriba. Vacío = orden solo alfabético con el resto."
        >
          <InputNumber style={{ width: '100%' }} min={1} step={1} placeholder="Opcional" />
        </Form.Item>

        <Form.Item name="asu_habilitado" label="Artículo Habilitado" valuePropName="checked">
          <Switch />
        </Form.Item>

        <Button type="primary" htmlType="submit" block>
          Guardar
        </Button>
      </Form>
    </Drawer>
  );
};

export default CFormularioArticuloSucursal;